import createNormalizeRemoteEvent from '@/lib/threadEvents/createNormalizeRemoteThreadEvent.js';
import { toPlainText as toSharedPlainText } from '@shared/utils';

const createId = () => {
  try {
    if (globalThis.crypto?.randomUUID) {
      return globalThis.crypto.randomUUID();
    }
  } catch {
    // fall through to timestamp-based ids
  }

  return `id-${Date.now()}-${Math.random().toString(16).slice(2)}`;
};

const createTimestamp = () => {
  return new Date().toISOString();
};

const toPlainText = (value) => {
  return toSharedPlainText(value, { pretty: true });
};

const toLines = (text) => {
  const normalized = String(text || '');

  if (!normalized) {
    return [{ text: '' }];
  }

  return normalized.split('\n').map((line) => {
    return {
      text: line
    };
  });
};

const normalizeMessageInputFileAttachments = ({ payload = {}, base = {} } = {}) => {
  const inputItemCandidates = [payload?.inputItems, payload?.input_items, base?.inputItems, base?.input_items];
  const normalizedAttachments = [];
  const seenAttachmentKeys = new Set();

  for (const inputItems of inputItemCandidates) {
    if (!Array.isArray(inputItems)) {
      continue;
    }

    for (const inputItem of inputItems) {
      if (!inputItem || typeof inputItem !== 'object' || Array.isArray(inputItem)) {
        continue;
      }

      const inputType = String(inputItem?.type || '')
        .trim()
        .toLowerCase();

      if (inputType !== 'file') {
        continue;
      }

      const attachment = inputItem?.attachment && typeof inputItem.attachment === 'object' && !Array.isArray(inputItem.attachment) ? inputItem.attachment : {};
      const attachmentId = String(inputItem?.attachmentId || inputItem?.attachment_id || '').trim() || null;
      const mediaKindRaw = String(inputItem?.mediaKind || inputItem?.media_kind || attachment?.mediaKind || attachment?.media_kind || '')
        .trim()
        .toLowerCase();
      const mediaKind = mediaKindRaw === 'image' ? 'image' : 'document';
      const mimeType = String(inputItem?.mimeType || inputItem?.mime_type || attachment?.mimeType || attachment?.mime_type || '').trim() || null;
      const attachmentPath = String(attachment?.path || '').trim();
      const revisionId = String(attachment?.revisionId || attachment?.revision_id || '').trim() || null;
      const filenameFromPath = attachmentPath ? attachmentPath.split('/').filter(Boolean).at(-1) || '' : '';
      const filename = String(inputItem?.filename || attachment?.filename || filenameFromPath || attachmentId || '').trim() || null;
      const sizeCandidate = Number(inputItem?.sizeBytes ?? inputItem?.size_bytes ?? attachment?.sizeBytes ?? attachment?.size_bytes);
      const sizeBytes = Number.isFinite(sizeCandidate) && sizeCandidate > 0 ? Math.floor(sizeCandidate) : null;
      const createdAt = String(attachment?.createdAt || attachment?.created_at || '').trim() || null;
      const downloadUrl = String(inputItem?.downloadUrl || inputItem?.download_url || attachment?.downloadUrl || attachment?.download_url || '').trim() || null;
      const conversionRaw = inputItem?.conversion && typeof inputItem.conversion === 'object' && !Array.isArray(inputItem.conversion) ? inputItem.conversion : attachment?.conversion && typeof attachment.conversion === 'object' && !Array.isArray(attachment.conversion) ? attachment.conversion : null;
      const conversion = conversionRaw
        ? {
            status: String(conversionRaw?.status || '').trim() || null,
            sourceFormat: String(conversionRaw?.sourceFormat || conversionRaw?.source_format || '').trim() || null,
            originalMimeType: String(conversionRaw?.originalMimeType || conversionRaw?.original_mime_type || '').trim() || null,
            targetMimeType: String(conversionRaw?.targetMimeType || conversionRaw?.target_mime_type || '').trim() || null,
            notice: String(conversionRaw?.notice || '').trim() || null
          }
        : null;
      const dedupeKey = attachmentId || `${mediaKind}:${mimeType || ''}:${filename || ''}:${sizeBytes || ''}`;

      if (!dedupeKey || seenAttachmentKeys.has(dedupeKey)) {
        continue;
      }

      seenAttachmentKeys.add(dedupeKey);
      normalizedAttachments.push({
        attachmentId,
        mediaKind,
        mimeType,
        filename,
        sizeBytes,
        path: attachmentPath || null,
        revisionId,
        createdAt,
        downloadUrl,
        ...(conversion
          ? {
              conversion
            }
          : {})
      });
    }
  }

  return normalizedAttachments;
};

const buildMessageBubbleText = ({ text = '' } = {}) => {
  return String(text || '').trim();
};

const normalizeRemoteThreadEventForPresentation = createNormalizeRemoteEvent({
  buildMessageBubbleText,
  clone: (value) => {
    try {
      return JSON.parse(JSON.stringify(value));
    } catch {
      return value;
    }
  },
  createId,
  createTimestamp,
  normalizeMessageInputFileAttachments,
  toLines,
  toPlainText
});

export default normalizeRemoteThreadEventForPresentation;
